import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import { subscriptions as mockSubscriptions, invoices as mockInvoices, type Subscription } from '@/mocks/superAdminBilling';
import { economyData } from '@/mocks/saEconomy';
import { renewals } from '@/mocks/superAdminRenewals';
import { payments as mockPayments } from '@/mocks/superAdminPayments';

type LoadingState = 'loading' | 'ready' | 'error';

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n}`;
}

function simulateLoad(): Promise<void> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (Math.random() > 0.95) reject(new Error('Timeout al conectar.'));
      else resolve();
    }, 600 + Math.random() * 400);
  });
}

export default function SuperAdminBilling() {
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const loadData = useCallback(() => {
    setLoadingState('loading'); setErrorMsg(null);
    simulateLoad()
      .then(() => setLoadingState('ready'))
      .catch((err) => { setErrorMsg(err.message || 'Error desconocido'); setLoadingState('error'); });
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const kpis = useMemo(() => {
    const activeSubs = mockSubscriptions.filter(s => s.status === 'active');
    const mrr = activeSubs.reduce((sum, s) => sum + s.monthlyPrice, 0);
    const arr = mrr * 12;
    const pendingPayments = mockPayments.filter(p => p.status === 'pending').reduce((s, p) => s + p.amount, 0);
    const overdueInvoices = mockInvoices.filter(i => i.status === 'overdue').length;
    const upcomingRenewals = renewals.filter(r => r.status === 'expiring_soon').length;
    const pendingPayouts = 42800; // mock
    const refunds = mockPayments.filter(p => p.status === 'refunded').reduce((s, p) => s + p.amount, 0);
    const disputes = 3; // mock
    const commissions = economyData.kpis.find(k => k.id === 'ingresos-rfw')?.rawValue || 0;
    const processorErrors = 1; // mock
    return { mrr, arr, pendingPayments, overdueInvoices, upcomingRenewals, pendingPayouts, refunds, disputes, commissions, processorErrors, activeSubs: activeSubs.length, totalSubs: mockSubscriptions.length };
  }, []);

  const expiredInvoices = useMemo(() => mockInvoices.filter(i => i.status === 'overdue').slice(0, 4), []);
  const failedPayments = useMemo(() => mockPayments.filter(p => p.status === 'failed').slice(0, 3), []);
  const upcomingR = useMemo(() => renewals.filter(r => r.status === 'expiring_soon').slice(0, 4), []);

  if (loadingState === 'loading') {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="flex"><SASidebar /><main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {Array.from({ length: 6 }).map((_, i) => (<div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 animate-pulse"><div className="w-10 h-10 rounded-xl bg-background-200"></div><div className="space-y-1.5"><div className="w-14 h-2.5 bg-background-200 rounded"></div><div className="w-10 h-3 bg-background-200 rounded"></div></div></div>))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (<div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-5 animate-pulse"><div className="w-32 h-4 bg-background-200 rounded mb-3"></div><div className="space-y-2">{Array.from({ length: 3 }).map((_, j) => (<div key={j} className="w-full h-8 bg-background-200 rounded-lg"></div>))}</div></div>))}
            </div>
          </div>
        </main></div>
      </div>
    );
  }

  if (loadingState === 'error') {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="flex"><SASidebar /><main className="flex-1 min-w-0 flex items-center justify-center p-4">
          <div className="bg-background-50 border border-background-200 rounded-2xl p-8 max-w-sm w-full text-center">
            <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4"><i className="ri-error-warning-line text-red-400 text-2xl"></i></div>
            <h2 className="text-foreground-950 font-bold text-base mb-1.5">Error al cargar datos</h2>
            <p className="text-foreground-400 text-xs mb-5">{errorMsg}</p>
            <button onClick={loadData} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-sm font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-refresh-line text-sm"></i>Reintentar</button>
          </div>
        </main></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Billing Overview</span>
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Billing Overview</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  {kpis.totalSubs} suscripciones · {kpis.activeSubs} activas · MRR {formatCurrency(kpis.mrr)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Link to="/sa/contracts" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-background-200 bg-background-50 text-foreground-600 text-xs font-semibold hover:bg-background-100 hover:border-foreground-300 transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-file-text-line text-sm"></i>Contratos
                </Link>
              </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {[
                { label: 'MRR', value: formatCurrency(kpis.mrr), icon: 'ri-bank-card-line', bg: 'bg-emerald-50', color: 'text-emerald-500', sub: 'mensual recurrente' },
                { label: 'ARR', value: formatCurrency(kpis.arr), icon: 'ri-line-chart-line', bg: 'bg-sky-50', color: 'text-sky-500', sub: 'anual proyectado' },
                { label: 'Pagos Pendientes', value: formatCurrency(kpis.pendingPayments), icon: 'ri-time-line', bg: 'bg-amber-50', color: 'text-amber-500', sub: 'por procesar' },
                { label: 'Facturas Vencidas', value: kpis.overdueInvoices, icon: 'ri-error-warning-line', bg: 'bg-red-50', color: 'text-red-400', sub: `${kpis.overdueInvoices} facturas` },
                { label: 'Comisiones RFW', value: formatCurrency(kpis.commissions), icon: 'ri-percent-line', bg: 'bg-violet-50', color: 'text-violet-500', sub: 'revenue share' },
                { label: 'Renovaciones Próx.', value: kpis.upcomingRenewals, icon: 'ri-calendar-check-line', bg: 'bg-rose-50', color: 'text-rose-400', sub: 'próximos 45 días' },
                { label: 'Payouts Pendientes', value: formatCurrency(kpis.pendingPayouts), icon: 'ri-exchange-funds-line', bg: 'bg-amber-50', color: 'text-amber-500', sub: 'a radios' },
                { label: 'Reembolsos', value: formatCurrency(kpis.refunds), icon: 'ri-arrow-go-back-line', bg: 'bg-red-50', color: 'text-red-400', sub: 'este mes' },
                { label: 'Disputas', value: kpis.disputes, icon: 'ri-alert-line', bg: 'bg-orange-50', color: 'text-orange-400', sub: 'activas' },
                { label: 'Errores Proc.', value: kpis.processorErrors, icon: 'ri-plug-line', bg: 'bg-stone-100', color: 'text-stone-500', sub: 'procesadores' },
              ].map((kpi) => (
                <div key={kpi.label} className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg ${kpi.bg} flex items-center justify-center shrink-0`}>
                    <i className={`${kpi.icon} ${kpi.color} text-sm`}></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-foreground-400 text-[10px] font-medium truncate">{kpi.label}</p>
                    <p className="text-foreground-800 text-sm font-bold">{kpi.value}</p>
                    <p className="text-foreground-300 text-[9px]">{kpi.sub}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Alerts + Activity grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Alertas Críticas */}
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-background-100 bg-red-50/30">
                  <div className="flex items-center gap-2">
                    <i className="ri-alert-line text-red-400 text-sm"></i>
                    <h3 className="text-foreground-800 text-xs font-bold">Alertas Críticas</h3>
                  </div>
                  <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-[9px] font-bold">4</span>
                </div>
                <div className="divide-y divide-background-100">
                  {[
                    { icon: 'ri-error-warning-line', color: 'text-red-400', text: '3 facturas vencidas con más de 30 días', bg: 'bg-red-50' },
                    { icon: 'ri-plug-line', color: 'text-amber-500', text: 'PayPal webhook reportó error en último sync', bg: 'bg-amber-50' },
                    { icon: 'ri-time-line', color: 'text-amber-500', text: '2 renovaciones en período de gracia por vencer', bg: 'bg-amber-50' },
                    { icon: 'ri-bank-card-line', color: 'text-red-400', text: 'Pago rechazado: Radio Élite Digital ($49.95)', bg: 'bg-red-50' },
                  ].map((a, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                      <div className={`w-7 h-7 rounded-lg ${a.bg} flex items-center justify-center shrink-0`}>
                        <i className={`${a.icon} ${a.color} text-xs`}></i>
                      </div>
                      <span className="text-foreground-600 text-[10px]">{a.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actividad Reciente */}
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-background-100">
                  <div className="flex items-center gap-2">
                    <i className="ri-history-line text-foreground-500 text-sm"></i>
                    <h3 className="text-foreground-800 text-xs font-bold">Actividad Financiera Reciente</h3>
                  </div>
                </div>
                <div className="divide-y divide-background-100">
                  {[
                    { text: 'Pago recibido: Radio Nueva Actitud', amount: '$49.95', time: 'Hace 2h', color: 'text-emerald-600', bg: 'bg-emerald-50' },
                    { text: 'Factura generada: TechBeats #INV-2026-0042', amount: '$49.95', time: 'Hace 5h', color: 'text-sky-600', bg: 'bg-sky-50' },
                    { text: 'Renovación procesada: Stereo Visión', amount: '$49.95', time: 'Hace 8h', color: 'text-emerald-600', bg: 'bg-emerald-50' },
                    { text: 'Reembolso emitido: Radio Jazz Nocturno', amount: '-$29.00', time: 'Ayer', color: 'text-red-500', bg: 'bg-red-50' },
                    { text: 'Payout enviado: Radio Deportiva Total', amount: '$3,200', time: 'Ayer', color: 'text-amber-600', bg: 'bg-amber-50' },
                    { text: 'Comisión RFW aplicada: 12 campañas', amount: '$1,840', time: 'Hace 2 días', color: 'text-violet-600', bg: 'bg-violet-50' },
                  ].map((act, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-2.5">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${act.color.replace('text', 'bg')}`}></div>
                        <span className="text-foreground-600 text-[10px] truncate">{act.text}</span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className={`text-[10px] font-bold ${act.color}`}>{act.amount}</span>
                        <span className="text-foreground-300 text-[9px] w-12 text-right">{act.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Próximas Renovaciones + Facturas Vencidas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Próximas Renovaciones */}
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-background-100">
                  <div className="flex items-center gap-2">
                    <i className="ri-calendar-check-line text-amber-500 text-sm"></i>
                    <h3 className="text-foreground-800 text-xs font-bold">Próximas Renovaciones</h3>
                  </div>
                  <Link to="/sa/plans" className="text-foreground-400 text-[9px] font-medium hover:text-foreground-600 cursor-pointer whitespace-nowrap">Ver todas <i className="ri-arrow-right-s-line text-[9px]"></i></Link>
                </div>
                <div className="divide-y divide-background-100">
                  {upcomingR.length === 0 ? (
                    <div className="px-4 py-8 text-center"><p className="text-foreground-400 text-xs">Sin renovaciones próximas</p></div>
                  ) : upcomingR.map((r) => (
                    <div key={r.id} className="flex items-center justify-between px-4 py-2.5">
                      <div className="min-w-0 flex-1">
                        <p className="text-foreground-800 text-[10px] font-semibold truncate">{r.radioName}</p>
                        <p className="text-foreground-400 text-[9px]">{r.frequency} · {r.plan}</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-foreground-800 text-[10px] font-bold">${r.amount.toFixed(2)}</span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-amber-50 text-amber-600">{r.daysRemaining} días</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Facturas Vencidas */}
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-background-100">
                  <div className="flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-400 text-sm"></i>
                    <h3 className="text-foreground-800 text-xs font-bold">Facturas Vencidas</h3>
                  </div>
                  <Link to="/sa/invoices" className="text-foreground-400 text-[9px] font-medium hover:text-foreground-600 cursor-pointer whitespace-nowrap">Ver todas <i className="ri-arrow-right-s-line text-[9px]"></i></Link>
                </div>
                <div className="divide-y divide-background-100">
                  {expiredInvoices.length === 0 ? (
                    <div className="px-4 py-8 text-center"><p className="text-foreground-400 text-xs">Sin facturas vencidas</p></div>
                  ) : expiredInvoices.map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between px-4 py-2.5">
                      <div className="min-w-0 flex-1">
                        <p className="text-foreground-800 text-[10px] font-semibold truncate">{inv.id} · {inv.radioName}</p>
                        <p className="text-foreground-400 text-[9px]">{inv.frequency} · Vence: {inv.dueDate}</p>
                      </div>
                      <span className="text-red-500 text-[10px] font-bold">${inv.amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Pagos Fallidos + Payouts Pendientes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-background-100">
                  <div className="flex items-center gap-2">
                    <i className="ri-close-circle-line text-red-400 text-sm"></i>
                    <h3 className="text-foreground-800 text-xs font-bold">Pagos Fallidos</h3>
                  </div>
                  <Link to="/sa/payments" className="text-foreground-400 text-[9px] font-medium hover:text-foreground-600 cursor-pointer whitespace-nowrap">Ver todos <i className="ri-arrow-right-s-line text-[9px]"></i></Link>
                </div>
                <div className="divide-y divide-background-100">
                  {failedPayments.length === 0 ? (
                    <div className="px-4 py-8 text-center"><p className="text-foreground-400 text-xs">Sin pagos fallidos</p></div>
                  ) : failedPayments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between px-4 py-2.5">
                      <div className="min-w-0 flex-1">
                        <p className="text-foreground-800 text-[10px] font-semibold truncate">{p.radioName}</p>
                        <p className="text-foreground-400 text-[9px]">{p.frequency} · {p.method}</p>
                      </div>
                      <span className="text-red-500 text-[10px] font-bold">${p.amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-background-100">
                  <div className="flex items-center gap-2">
                    <i className="ri-exchange-funds-line text-amber-500 text-sm"></i>
                    <h3 className="text-foreground-800 text-xs font-bold">Payouts Pendientes</h3>
                  </div>
                  <Link to="/sa/payouts" className="text-foreground-400 text-[9px] font-medium hover:text-foreground-600 cursor-pointer whitespace-nowrap">Ver todos <i className="ri-arrow-right-s-line text-[9px]"></i></Link>
                </div>
                <div className="px-4 py-6 text-center">
                  <p className="text-foreground-800 text-sm font-bold">{formatCurrency(kpis.pendingPayouts)}</p>
                  <p className="text-foreground-400 text-[10px]">pendientes de pago a radios</p>
                  <p className="text-foreground-300 text-[9px] mt-1">Próximo corte: 15/07/2026</p>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
              <h3 className="text-foreground-950 text-xs font-bold mb-3 flex items-center gap-2">
                <i className="ri-compass-3-line text-accent-500"></i>
                Accesos Rápidos
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
                {[
                  { label: 'Plans & Licensing', icon: 'ri-stack-line', path: '/sa/plans', color: 'bg-emerald-50 text-emerald-600' },
                  { label: 'Invoices', icon: 'ri-file-list-3-line', path: '/sa/invoices', color: 'bg-sky-50 text-sky-600' },
                  { label: 'Payments', icon: 'ri-money-dollar-circle-line', path: '/sa/payments', color: 'bg-emerald-50 text-emerald-600' },
                  { label: 'Payouts', icon: 'ri-exchange-funds-line', path: '/sa/payouts', color: 'bg-amber-50 text-amber-600' },
                  { label: 'Proc. de Pago', icon: 'ri-bank-card-line', path: '/sa/payment-processors', color: 'bg-violet-50 text-violet-600' },
                  { label: 'Fees & Charges', icon: 'ri-price-tag-3-line', path: '/sa/fees-charges', color: 'bg-rose-50 text-rose-600' },
                  { label: 'Contratos', icon: 'ri-file-text-line', path: '/sa/contracts', color: 'bg-amber-50 text-amber-600' },
                ].map((nav) => (
                  <Link key={nav.label} to={nav.path} className="bg-background-100 rounded-xl p-3 flex items-center gap-2.5 hover:bg-background-200 transition-colors cursor-pointer">
                    <div className={`w-8 h-8 rounded-lg ${nav.color.split(' ')[0]} flex items-center justify-center shrink-0`}>
                      <i className={`${nav.icon} ${nav.color.split(' ')[1]} text-sm`}></i>
                    </div>
                    <span className="text-foreground-700 text-xs font-semibold whitespace-nowrap">{nav.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>

      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · Billing Overview</p>
      </div>
    </div>
  );
}